/**
 * ai-assistant data layer — the ONLY place this feature names the real `ai-assistant` endpoints
 * (server/src/modules/ai-assistant/routes.js, mounted under /api/v1) and their capability keys. Every
 * screen reads/writes through these hooks so the wire contract lives in one file. Server data lives
 * ONLY in TanStack Query (lab-scoped keys); mutations invalidate the keys they touch (L-1).
 *
 * Endpoints consumed (all under /api/v1, all capability-keyed, no public route):
 *   POST /assistant/sessions                 ai.assistant.query                 → { session_id }
 *   POST /assistant/sessions/:id/messages    ai.assistant.query                 → Answer (never-throws envelope)
 *   GET  /assistant/instructions             ai.assistant.query                 → Instructions | null
 *   PUT  /assistant/instructions             ai.assistant.instructions.manage   → { instructions_id, version }
 *
 * NOTE there is no GET-sessions / GET-messages route: the conversation is held client-side (each ask
 * returns ONE tagged assistant turn). The `repondre` answer NEVER throws — disabled/error/abstenu are
 * returned as a 200 envelope, not an HTTP error — so the chat surface degrades gracefully by status.
 */
import { api, qk, useApiMutation, useApiQuery } from "@/api";
import { useAuthStore } from "@/auth";
import type { QueryKey } from "@tanstack/react-query";
import type {
  Answer,
  Id,
  Instructions,
  OpenSessionResult,
  PublishInstructionsResult,
} from "./types";
import { idStr } from "./types";

/** Lab id for query scoping — from the live auth session (multi-tenant cache isolation). */
export function useLabId(): string | null {
  return useAuthStore((s) => s.user?.laboratoireId ?? s.lab?.id ?? null);
}

/** Lab-scoped query-key sub-factory for the ai-assistant feature. */
export const aiKeys = {
  instructions: (labId: string | null): QueryKey => qk.scoped(labId, "ai-assistant", "instructions"),
};

// ── Instructions.md (AAS-5) ─────────────────────────────────────────────────────────

/**
 * GET /assistant/instructions — the active per-lab instructions.md (or null when none published).
 * Local-resident: readable offline (the channel-down fallback). Cached hard — it changes only on a
 * publish, which invalidates this key.
 */
export function useInstructions(enabled: boolean) {
  const labId = useLabId();
  return useApiQuery<Instructions | null>(aiKeys.instructions(labId), "/assistant/instructions", {
    enabled,
    staleTime: 60_000,
  });
}

export interface PublishInstructionsVars {
  contenu_md: string;
}

/** PUT /assistant/instructions — publish a new version (append-only, version+1). Refreshes the active. */
export function usePublishInstructions() {
  const labId = useLabId();
  return useApiMutation<PublishInstructionsResult, PublishInstructionsVars>(
    (vars) => api.put<PublishInstructionsResult>("/assistant/instructions", vars),
    { invalidate: [aiKeys.instructions(labId)] },
  );
}

// ── Conversation (client-held; imperative, drives the chat surface) ───────────────────

/** POST /assistant/sessions — open a helpdesk session. Imperative (no list to cache). */
export function openSession(): Promise<OpenSessionResult> {
  return api.post<OpenSessionResult>("/assistant/sessions", {});
}

/** Mutation form of session-open so the screen gets isPending / error wiring for free. */
export function useOpenSession() {
  return useApiMutation<OpenSessionResult, void>(() => openSession());
}

export interface AskVars {
  sessionId: Id;
  requete: string;
}

/**
 * POST /assistant/sessions/:id/messages — ask the helpdesk. Returns the never-throws Answer envelope
 * (status ok|abstenu|disabled|error). No cache invalidation (the conversation is client-held).
 */
export function useAsk() {
  return useApiMutation<Answer, AskVars>(({ sessionId, requete }) =>
    api.post<Answer>(`/assistant/sessions/${idStr(sessionId)}/messages`, { requete }),
  );
}
